import { supabase } from "../supabase/supabase";

// What every daily game on /home shares: which day it is, a random order that
// is the same on every phone that day, a 10 second clock, and the record of
// what this device has played.
//
// The day is the Eastern calendar day, the clock the rest of the app runs on,
// so a new game opens at midnight Eastern everywhere. Everyone gets the same
// questions on the same day because the order comes from a seed built from
// the date, not from Math.random: a shared "8/10" means the same ten
// questions.
//
// Plays are kept on the device, and the game reads only that copy. A finished
// play is also reported to game_plays (migration 080) so uptake can be
// counted, but that is a count, not the record. That makes "once a day"
// a rule a determined player can get round by clearing site data, which is
// fine for a game whose score nobody wins anything for. A league leaderboard
// would need the scores stored server side.

// Accounts that may replay a game as often as they like, for testing. Each
// replay gets its own questions (seedDay). Keyed by users.id.
const GAME_TESTERS = new Set([
  "92eda546-daf2-4b28-89dd-067dd2be833b",
]);

export const isGameTester = (userId) => Boolean(userId) && GAME_TESTERS.has(userId);

/** The seed for a day's game: the day itself, or the day and the replay. */
export const seedDay = (day, run = 0) => (run ? `${day}#${run}` : day);

/** A fresh play after a tester's finished one, with new questions. */
export const nextRun = (play) => ({ answers: [], done: false, pending: null, run: (play.run || 0) + 1 });

// Each game sets its own number of questions (QUESTIONS in its module).
export const SECONDS_PER_QUESTION = 10;

const EASTERN = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/New_York",
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "numeric", minute: "numeric", second: "numeric", hourCycle: "h23",
});

const easternParts = (now) =>
  Object.fromEntries(EASTERN.formatToParts(now).map((p) => [p.type, p.value]));

/** "2026-10-04": the Eastern calendar day, which names the day's game. */
export function dayKey(now = new Date()) {
  const p = easternParts(now);
  return `${p.year}-${p.month}-${p.day}`;
}

/** Milliseconds until the next game opens, at midnight Eastern. */
export function msUntilNextGame(now = new Date()) {
  const p = easternParts(now);
  const elapsed = ((parseInt(p.hour, 10) % 24) * 3600 + parseInt(p.minute, 10) * 60 + parseInt(p.second, 10)) * 1000;
  return 24 * 3600 * 1000 - elapsed;
}

/** "9h", "45m": how long until the next game, for the tiles on /home. */
export function formatNextGame(ms) {
  const minutes = Math.max(1, Math.ceil(ms / 60000));
  return minutes >= 60 ? `${Math.floor(minutes / 60)}h` : `${minutes}m`;
}

/** "Sun 4 Oct", for the share line and the game header. */
export function formatDay(key) {
  const [y, m, d] = key.split("-").map(Number);
  // Noon UTC lands on the same calendar day in every US time zone.
  const date = new Date(Date.UTC(y, m - 1, d, 12));
  const weekday = date.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
  const month = date.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  return `${weekday} ${d} ${month}`;
}

// A small seeded generator (mulberry32) and a string hash to seed it. Not for
// anything secret: it only has to give every device the same sequence.
function hashString(text) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function seededRandom(seed) {
  let a = hashString(String(seed));
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A copy of `items` in an order fixed by `random`. */
export function shuffle(items, random) {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** "🟩🟩🟥": one square per answered question. */
export const squares = (answers) => answers.map((ok) => (ok ? "🟩" : "🟥")).join("");

// --- What this device has played ---
//
// Every read and write is wrapped: storage can be missing or refuse a write
// (private browsing, a full disk), and the game must still play.

const playKey = (game, day) => `game:${game}:${day}`;
const practiceKey = (game) => `game:${game}:practiced`;

/**
 * Today's play, or a fresh one. `answers` holds true or false per question
 * answered. `pending` is the question on screen when the play was last saved:
 * if it is still unanswered on the next load, the player left mid-question,
 * and that counts as running out of time, the same as leaving the app.
 */
export function loadPlay(game, day) {
  try {
    const saved = JSON.parse(localStorage.getItem(playKey(game, day)));
    if (saved && Array.isArray(saved.answers)) {
      return { answers: saved.answers, done: Boolean(saved.done), pending: saved.pending ?? null, run: saved.run || 0 };
    }
  } catch {
    /* nothing saved, or storage unavailable */
  }
  return { answers: [], done: false, pending: null, run: 0 };
}

export function savePlay(game, day, play) {
  try {
    localStorage.setItem(playKey(game, day), JSON.stringify(play));
  } catch {
    /* the game still plays; it just will not be remembered */
  }
}

export function hasPractised(game) {
  try {
    return localStorage.getItem(practiceKey(game)) === "1";
  } catch {
    return false;
  }
}

export function markPractised(game) {
  try {
    localStorage.setItem(practiceKey(game), "1");
  } catch {
    /* shown again next time, which is harmless */
  }
}

// --- Telling the server a game was finished ---
//
// Counts players per game per day. Fire and forget: the game reads its state
// from localStorage and must never wait on, or fail because of, this write.
// The unique index on (user_id, game, day) makes repeats harmless, so the
// caller does not need to remember that it already sent one.

export async function recordPlay(userId, game, day, answers) {
  if (!userId) return;
  try {
    const score = answers.filter(Boolean).length;
    await supabase
      .from("game_plays")
      .upsert({ user_id: userId, game, day, score }, { onConflict: "user_id,game,day", ignoreDuplicates: true });
  } catch {
    /* offline or refused: the game still counts for the player */
  }
}
